import { mergeClassNames } from '../../../utils/mergeClassNames';
import { ButtonContext } from '../../navigation/Button/Button';
import { IconButtonContext } from '../../navigation/IconButton/IconButton';
import { useMemo } from 'react';
import {
  Provider,
  Menu as ReactAriaMenu,
  MenuItem as ReactAriaMenuItem,
  MenuItemProps as ReactAriaMenuItemProps,
  MenuProps as ReactAriaMenuProps,
  MenuTrigger as ReactAriaMenuTrigger,
  MenuTriggerProps as ReactAriaMenuTriggerProps,
  Popover as ReactAriaPopover,
  PopoverProps as ReactAriaPopoverProps,
  Separator as ReactAriaSeparator,
} from 'react-aria-components';
import { MenuContext, useMenuContext, type MenuSize } from './MenuContext';
import styles from './MenuTrigger.module.scss';

export type BetaMenuTriggerRootProps = ReactAriaMenuTriggerProps & {
  /**
   * The size of the menu and its subcomponents.
   * @default 'medium'
   */
  size?: MenuSize;
};

/**
 * `BetaMenuTrigger` is used to display a list of actions or options that a user can choose.
 *
 * Built with React Aria's
 * [Popover](https://react-spectrum.adobe.com/react-aria/Popover.html) and
 * [Menu](https://react-spectrum.adobe.com/react-aria/Menu.html) components.
 */
export const BetaMenuTriggerRoot = ({
  size = 'medium',
  ...props
}: BetaMenuTriggerRootProps) => {
  const contextValue = useMemo(() => ({ size }), [size]);

  const buttonProps = { size };
  const iconButtonProps = { size: size === 'compact' ? 'small' : size };

  return (
    <Provider
      values={[
        [ButtonContext, buttonProps],
        [IconButtonContext, iconButtonProps],
      ]}
    >
      <MenuContext.Provider value={contextValue}>
        <ReactAriaMenuTrigger {...props} />
      </MenuContext.Provider>
    </Provider>
  );
};

export type BetaMenuTriggerPopoverProps = ReactAriaPopoverProps;

export const BetaMenuTriggerPopover = ({
  className,
  ...props
}: BetaMenuTriggerPopoverProps) => {
  return (
    <ReactAriaPopover
      {...props}
      className={(renderProps) =>
        mergeClassNames([styles.popover, className], renderProps)
      }
    />
  );
};

export type BetaMenuTriggerListProps<T> = ReactAriaMenuProps<T>;

export const BetaMenuTriggerList = <T extends object>(
  props: BetaMenuTriggerListProps<T>
) => {
  return <ReactAriaMenu className={styles.list} {...props} />;
};

export type BetaMenuTriggerItemProps = ReactAriaMenuItemProps & {
  /** The visual style of the `BetaMenuTriggerItem`. */
  variant?: 'primary' | 'danger';
};

export const BetaMenuTriggerItem = ({
  variant = 'primary',
  className,
  ...props
}: BetaMenuTriggerItemProps) => {
  const { size } = useMenuContext();

  return (
    <ReactAriaMenuItem
      {...props}
      data-size={size}
      data-variant={variant}
      className={mergeClassNames([styles.item, className])}
    />
  );
};

export const BetaMenuTriggerSeparator = () => {
  return <ReactAriaSeparator className={styles.separator} />;
};
